import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ServiceManagementPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [services, setServices] = useState([]);
  const [edit, setEdit] = useState(null);

  async function load() {
    const { data } = await api.get("/services");
    setServices(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEdit({ name_id: "", name_ar: "", name_en: "", description_id: "", description_ar: "", description_en: "", price: 0, duration_min: 60, is_vip: false, isNew: true }); }
  const openEdit = (s) => setEdit({ ...s });

  async function save() {
    try {
      if (edit.isNew) await api.post("/services", edit);
      else await api.put(`/services/${edit.id}`, edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  async function del(s) {
    if (!window.confirm(`Delete ${s.name_id}?`)) return;
    await api.delete(`/services/${s.id}`);
    toast.success("Deleted"); load();
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— Catalog</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("services.title")}</h1>
        </div>
        {canEdit && (
          <button onClick={openNew} data-testid="service-add-btn" className="btn-gold">
            <Plus className="h-4 w-4" /> {t("common.add")}
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.id} className="card-luxury p-5" data-testid={`service-item-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-foreground/40">{s.duration_min} {t("common.minutes")}</div>
                <h3 className="text-lg font-heading mt-1">{s.name_id}</h3>
                <p className="text-sm text-foreground/60" dir="rtl">{s.name_ar}</p>
              </div>
              {s.is_vip && <Crown className="h-4 w-4 text-gold" />}
            </div>
            {(s.description_id || s.description_ar || s.description_en) && (
              <p className="mt-3 text-xs text-foreground/50 line-clamp-2" title={s.description_id || s.description_en || s.description_ar}>
                {s.description_id || s.description_en || s.description_ar}
              </p>
            )}
            <div className="mt-4 pt-4 border-t border-gold/10 flex justify-between items-center">
              <span className="text-xl text-gold font-heading">{formatSAR(s.price)}</span>
              {canEdit && (
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`} className="p-2 rounded-full hover:bg-gold/10 text-gold">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(s)} data-testid={`service-delete-${s.id}`} className="p-2 rounded-full hover:bg-red-500/10 text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEdit(null)}>
          <div className="card-luxury p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-heading">{edit.isNew ? t("common.add") : t("common.edit")}</h2>
              <button onClick={() => setEdit(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("services.nameId")}</label>
                <input value={edit.name_id} onChange={(e) => setEdit({ ...edit, name_id: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("services.nameAr")}</label>
                <input dir="rtl" value={edit.name_ar} onChange={(e) => setEdit({ ...edit, name_ar: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("services.nameEn")}</label>
                <input value={edit.name_en || ""} onChange={(e) => setEdit({ ...edit, name_en: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">
                  Keterangan (Indonesia)
                </label>
                <textarea
                  value={edit.description_id || ""}
                  onChange={(e) => setEdit({ ...edit, description_id: e.target.value })}
                  data-testid="service-desc-id"
                  rows={3}
                  placeholder="Contoh: Sesi relaksasi lengkap dengan minyak aromaterapi selama 60 menit."
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm resize-y"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">
                  Keterangan (Arabic) — الوصف
                </label>
                <textarea
                  dir="rtl"
                  value={edit.description_ar || ""}
                  onChange={(e) => setEdit({ ...edit, description_ar: e.target.value })}
                  data-testid="service-desc-ar"
                  rows={3}
                  placeholder="وصف الخدمة بالعربية"
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm resize-y"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">
                  Keterangan (English)
                </label>
                <textarea
                  value={edit.description_en || ""}
                  onChange={(e) => setEdit({ ...edit, description_en: e.target.value })}
                  data-testid="service-desc-en"
                  rows={3}
                  placeholder="Short service description in English."
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm resize-y"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("services.price")}</label>
                <input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: Number(e.target.value) })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("services.duration")}</label>
                <input type="number" value={edit.duration_min} onChange={(e) => setEdit({ ...edit, duration_min: Number(e.target.value) })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!edit.is_vip} onChange={(e) => setEdit({ ...edit, is_vip: e.target.checked })} className="accent-gold h-4 w-4" />
                {t("services.vip")}
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEdit(null)} className="btn-outline-gold flex-1 justify-center">{t("common.cancel")}</button>
              <button onClick={save} data-testid="service-save-btn" className="btn-gold flex-1 justify-center">{t("common.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
